// Small formatters the viewer and its renderers share. Binary units for bytes,
// because every producer of these figures counts in powers of two.

export function formatBytes(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) return '';
  if (n < 1024) return `${n} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let v = n / 1024;
  let u = 0;
  while (v >= 1024 && u < units.length - 1) { v /= 1024; u++; }
  return `${v < 10 ? v.toFixed(1) : Math.round(v)} ${units[u]}`;
}

/** Seconds as h:mm:ss, or m:ss under an hour. */
export function formatDuration(seconds: number | null | undefined): string {
  if (seconds == null || !Number.isFinite(seconds)) return '';
  const s = Math.max(0, Math.round(seconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const r = s % 60;
  const two = (n: number) => String(n).padStart(2, '0');
  return h > 0 ? `${h}:${two(m)}:${two(r)}` : `${m}:${two(r)}`;
}

export function formatAspect(w: number, h: number): string {
  if (!w || !h) return '';
  const g = gcd(w, h);
  const a = w / g;
  const b = h / g;
  // 1366×768 reduces to 683:384, which reads as nothing. Round to the nearest common ratio when close.
  const common: Array<[number, number]> = [[1, 1], [4, 3], [3, 2], [16, 9], [16, 10], [21, 9], [2, 3], [3, 4], [9, 16], [10, 16], [5, 4], [4, 5]];
  const ratio = w / h;
  for (const [x, y] of common) if (Math.abs(ratio - x / y) < 0.01) return `${x}:${y}`;
  return a > 100 || b > 100 ? `${ratio.toFixed(2)}:1` : `${a}:${b}`;
}

function gcd(a: number, b: number): number {
  return b === 0 ? a : gcd(b, a % b);
}

/** A classic hex dump of the first bytes: offset, sixteen bytes, ASCII. */
export function hexDump(bytes: Uint8Array, max = 512): string {
  const n = Math.min(bytes.length, max);
  const lines: string[] = [];
  for (let off = 0; off < n; off += 16) {
    const chunk = bytes.subarray(off, Math.min(off + 16, n));
    const hex = Array.from(chunk, (b) => b.toString(16).padStart(2, '0')).join(' ');
    const text = Array.from(chunk, (b) => (b >= 32 && b < 127 ? String.fromCharCode(b) : '.')).join('');
    lines.push(`${off.toString(16).padStart(8, '0')}  ${hex.padEnd(47)}  ${text}`);
  }
  return lines.join('\n');
}

export async function sha256Hex(blob: Blob): Promise<string> {
  const buf = await blob.arrayBuffer();
  const digest = await crypto.subtle.digest('SHA-256', buf);
  return Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, '0')).join('');
}

export function hostOf(url: string): string {
  try { return new URL(url, typeof location !== 'undefined' ? location.href : undefined).host; } catch { return ''; }
}

export function isAbsoluteHttpUrl(url: string): boolean {
  try {
    const u = new URL(url);
    return u.protocol === 'https:' || u.protocol === 'http:';
  } catch {
    return false;
  }
}

/** Decode a text blob: UTF-8 first, strictly; a UTF-16 BOM; then Windows-1252 as the last resort. */
export async function decodeText(blob: Blob): Promise<{ text: string; encoding: string }> {
  const buf = await blob.arrayBuffer();
  const b = new Uint8Array(buf);
  if (b.length >= 2 && ((b[0] === 0xff && b[1] === 0xfe) || (b[0] === 0xfe && b[1] === 0xff))) {
    const enc = b[0] === 0xff ? 'utf-16le' : 'utf-16be';
    return { text: new TextDecoder(enc).decode(buf), encoding: enc.toUpperCase() };
  }
  try {
    return { text: new TextDecoder('utf-8', { fatal: true }).decode(buf), encoding: 'UTF-8' };
  } catch {
    return { text: new TextDecoder('windows-1252').decode(buf), encoding: 'Windows-1252' };
  }
}

/** RFC 4180 with a sniffed delimiter: quotes, doubled quotes, CRLF, and a ragged last line. */
export function parseDelimited(text: string, delimiter?: string): { rows: string[][]; delimiter: string } {
  const firstLine = text.slice(0, text.indexOf('\n') < 0 ? undefined : text.indexOf('\n'));
  const d = delimiter ?? (['\t', ';', '|', ','] as const).reduce((best, cand) =>
    (firstLine.split(cand).length > firstLine.split(best).length ? cand : best), ',');
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = '';
  let quoted = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (quoted) {
      if (c === '"') {
        if (text[i + 1] === '"') { cell += '"'; i++; } else quoted = false;
      } else cell += c;
      continue;
    }
    if (c === '"') { quoted = true; continue; }
    if (c === d) { row.push(cell); cell = ''; continue; }
    if (c === '\r') continue;
    if (c === '\n') { row.push(cell); rows.push(row); row = []; cell = ''; continue; }
    cell += c;
  }
  if (cell.length > 0 || row.length > 0) { row.push(cell); rows.push(row); }
  return { rows, delimiter: d };
}

/**
 * Where JSON.parse gave up, as a line and column. Engines differ in what they
 * put in the message: some carry a position, some a line and column, and the
 * newest carry neither for an unexpected token. When the message says nothing
 * a small scanner walks the text and reports the first byte it cannot accept.
 */
export function jsonErrorPosition(text: string, message: string): { line: number; column: number } | null {
  const lc = /line (\d+) column (\d+)/i.exec(message);
  if (lc) return { line: Number(lc[1]), column: Number(lc[2]) };
  const pos = /position (\d+)/i.exec(message);
  const offset = pos ? Number(pos[1]) : scanJson(text);
  if (offset == null) return null;
  const before = text.slice(0, offset);
  const line = before.split('\n').length;
  const column = offset - before.lastIndexOf('\n');
  return { line, column };
}

/** The offset of the first byte a strict JSON grammar rejects, or null when the text is valid. */
export function scanJson(text: string): number | null {
  let i = 0;
  const n = text.length;
  const ws = () => { while (i < n && (text[i] === ' ' || text[i] === '\n' || text[i] === '\r' || text[i] === '\t')) i++; };
  const fail = (at: number): never => { throw at; };
  const value = (): void => {
    ws();
    if (i >= n) fail(i);
    const c = text[i];
    if (c === '{') {
      i++; ws();
      if (text[i] === '}') { i++; return; }
      for (;;) {
        ws();
        if (text[i] !== '"') fail(i);
        str();
        ws();
        if (text[i] !== ':') fail(i);
        i++;
        value();
        ws();
        if (text[i] === ',') { i++; continue; }
        if (text[i] === '}') { i++; return; }
        fail(i);
      }
    }
    if (c === '[') {
      i++; ws();
      if (text[i] === ']') { i++; return; }
      for (;;) {
        value();
        ws();
        if (text[i] === ',') { i++; continue; }
        if (text[i] === ']') { i++; return; }
        fail(i);
      }
    }
    if (c === '"') { str(); return; }
    if (c === 't') { if (text.startsWith('true', i)) { i += 4; return; } fail(i); }
    if (c === 'f') { if (text.startsWith('false', i)) { i += 5; return; } fail(i); }
    if (c === 'n') { if (text.startsWith('null', i)) { i += 4; return; } fail(i); }
    const m = /^-?(0|[1-9]\d*)(\.\d+)?([eE][+-]?\d+)?/.exec(text.slice(i, i + 64));
    if (m) { i += m[0].length; return; }
    fail(i);
  };
  const str = (): void => {
    i++;
    while (i < n) {
      const c = text[i];
      if (c === '"') { i++; return; }
      if (c === '\\') { i += 2; continue; }
      if (c === '\n') fail(i);
      i++;
    }
    fail(n);
  };
  try {
    value();
    ws();
    if (i < n) return i;
    return null;
  } catch (at) {
    return typeof at === 'number' ? at : null;
  }
}
